import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

const StyledLabel = styled('div')(({ theme }) => ({
  borderRadius: '4px',
  padding: '6px 8px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',

  background: theme.palette.background.primaryGrey2,
}));

export default function CandidateRow({
  candidate,
  filters,
  selectedFilters,
  onViewRow,
}) {
  const {
    id,
    name,
    email,
    currentRole,
    currentOrg,
    source,
    tags,
    skills,
    lastEducationDegree,
    lastEducationInstitute,
    experience,
    appliedFor,
    domains,
    countryOfResidence,
    currentLocationCity,
  } = candidate;
  const popover = usePopover();
  const router = useRouter();

  const getExperience = () => {
    if (!experience) return '';
    if (experience === 1) return '1 year of experience';
    return `${experience} years of experience`;
  };

  const getDomains = () => {
    if (!domains) return '';
    if (domains.length === 1) return `${domains[0]}`;
    return `${domains.join(', ')}`;
  };

  const getLocation = () => {
    if (!countryOfResidence && !currentLocationCity) return '';
    if (!countryOfResidence && currentLocationCity) return currentLocationCity;
    if (countryOfResidence && !currentLocationCity) return countryOfResidence;
    if (countryOfResidence && currentLocationCity)
      return `${countryOfResidence}, ${currentLocationCity}`;
    return '';
  };

  const getFilterName = (filterCode, group) => {
    const filter = filters[group].find(_filter => _filter.code === filterCode);
    return filter ? filter.name : '';
  };

  const sortSelectedFilters = _skills => {
    const _selectedFilters = selectedFilters.map(filter =>
      getFilterName(filter, 'SKILLS'),
    );

    const _selectedSkills = _skills.filter(skill =>
      _selectedFilters.some(
        selectedFilter => selectedFilter.toLowerCase() === skill.toLowerCase(),
      ),
    );

    const _unSelectedSkills = _skills.filter(
      skill =>
        !_selectedFilters.some(
          selectedFilter =>
            selectedFilter.toLowerCase() === skill.toLowerCase(),
        ),
    );

    return _selectedSkills.concat(_unSelectedSkills);
  };

  return (
    <Box
      key={id}
      onClick={onViewRow}
      sx={{
        borderRadius: '8px',
        px: '16px',
        py: '24px',
        height: '195px',
        border: theme => `1px solid ${theme.palette.border.primaryBlack3}`,
        bgcolor: theme => theme.palette.background.primaryWhite1,
        cursor: 'pointer',
      }}
    >
      <Stack spacing="32px">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing="12px">
            <Avatar>
              {name
                ? name.charAt(0).toUpperCase()
                : email.charAt(0).toUpperCase()}
            </Avatar>
            <Stack spacing="4px">
              <Typography variant="subheading2">{name || email}</Typography>
              <Typography
                variant="caption"
                color="content.primaryBlack3"
                sx={{
                  maxWidth: '300px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {getExperience()}

                {getExperience() && getDomains() ? ' • ' : ''}

                {getDomains()}

                {(getDomains() && getLocation()) ||
                (getExperience() && getLocation())
                  ? ' • '
                  : ''}

                {getLocation()}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" spacing="16px">
            {appliedFor && appliedFor.length && (
              <Stack direction="row" alignItems="center" spacing="4px">
                <Typography variant="input">Applied for</Typography>

                <Typography key="applied-job" variant="input" fontWeight={500}>
                  {`${appliedFor[0].jobName}`}
                </Typography>
              </Stack>
            )}
            <IconButton
              onClick={event => {
                popover.onOpen(event);
                event.stopPropagation();
              }}
            >
              <Iconify icon="tabler:dots-vertical" />
            </IconButton>
          </Stack>
        </Stack>
        <Stack spacing="15px">
          <Stack
            height="32px"
            direction="row"
            alignItems="center"
            spacing="8px"
          >
            {(() => {
              if (!currentRole && !currentOrg) return null;
              const title = `${currentRole}, ${currentOrg}`;
              return (
                <Tooltip title={title}>
                  <StyledLabel>
                    <Iconify icon="tabler:briefcase-2" />
                    <Typography
                      sx={{
                        maxWidth: '300px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                      variant="input"
                    >
                      {title}
                    </Typography>
                  </StyledLabel>
                </Tooltip>
              );
            })()}
            {(() => {
              if (!lastEducationDegree && !lastEducationInstitute) return null;
              const title = `${lastEducationDegree}, ${lastEducationInstitute}`;
              return (
                <Tooltip title={title}>
                  <StyledLabel>
                    <Iconify icon="tabler:book" />
                    <Typography
                      sx={{
                        maxWidth: '300px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        lineHeight: 'normal',
                      }}
                      variant="input"
                    >
                      {title}
                    </Typography>
                  </StyledLabel>
                </Tooltip>
              );
            })()}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              height="24px"
              direction="row"
              alignItems="center"
              spacing="8px"
            >
              {skills && skills.length ? (
                <>
                  {sortSelectedFilters(skills)
                    .slice(0, 4)
                    .map((skill, index) => {
                      const isSelected = filters?.SKILLS ? filters.SKILLS.find(filter => {
                        if (selectedFilters.includes(filter.code)) {
                          if (
                            skill.toLowerCase() === filter.name.toLowerCase()
                          ) {
                            return true;
                          }
                          return false;
                        }
                        return false;
                      }): false

                      return (
                        <Tooltip title={skill} key={index}>
                          <StyledLabel
                            sx={{
                              px: '10px',
                              py: '4px',
                              bgcolor:
                                isSelected && 'background.primaryAccent2',
                            }}
                          >
                            {isSelected && <Iconify icon="tabler:check" />}
                            <Typography
                              sx={{
                                maxWidth: '130px',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                              variant="caption"
                            >
                              {skill}
                            </Typography>
                          </StyledLabel>
                        </Tooltip>
                      );
                    })}
                  {(() => {
                    const currLength = skills.slice(0, 4).length;

                    if (currLength < skills.length) {
                      return (
                        <Tooltip title={skills.slice(currLength).join(', ')}>
                          <StyledLabel
                            sx={{
                              px: '6px',
                              py: '4px',
                            }}
                          >
                            <Typography
                              sx={{
                                maxWidth: '130px',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                              variant="caption"
                            >
                              {`${skills.length - currLength} +`}
                            </Typography>
                          </StyledLabel>
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}{' '}
                </>
              ) : null}
            </Stack>
            <Stack direction="row" alignItems="center" spacing="9px">
              {source && (
                <Label
                  sx={{
                    color: theme => theme.palette.content.primaryBlack1,
                    bgcolor: theme => theme.palette.background.primaryBlue1,
                  }}
                >
                  {source}
                </Label>
              )}
              {tags && tags.length
                ? tags.map(tag => (
                    <Label
                      key={tag}
                      color="info"
                      sx={{
                        color: theme => theme.palette.content.primaryBlack1,
                      }}
                    >
                      {tag}
                    </Label>
                  ))
                : null}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <CustomPopover
        open={popover.open}
        onClose={event => {
          popover.onClose();
          event.stopPropagation();
        }}
        sx={{ width: 160 }}
      >
        <MenuItem
          onClick={() => {
            onViewRow();
            popover.onClose();
          }}
        >
          View candidate
        </MenuItem>
        <MenuItem
          onClick={() => {
            router.push(paths.dashboard.candidates.edit(id));
            popover.onClose();
          }}
        >
          Edit candidate
        </MenuItem>

        {/* <MenuItem>Update tags</MenuItem>
        <MenuItem>Move to current job</MenuItem>
        <MenuItem>Invite for job</MenuItem>
        <MenuItem>Archive candidate</MenuItem> */}
      </CustomPopover>
    </Box>
  );
}

CandidateRow.propTypes = {
  candidate: PropTypes.object.isRequired,
  onViewRow: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  selectedFilters: PropTypes.array.isRequired,
};
